import { clearDraftRecoveries } from '@/pages/menu-editor-v2/hooks/power-draft/recovery'
import { clearPosEntryRecoveries } from '@/features/pos-ordering/recovery'
import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { apiClient } from '@/api/client'
import { API_BASE_URL } from '@/api/config'
import { saveCredentials, loadCredentials, clearCredentials } from '@/utils/credentialStorage'
import { isDeviceDiagnosticsSupported, startDeviceDiagnostics } from '@/lib/deviceDiagnostics'
import { startAppUpdates } from '@/lib/appUpdate'
import { enrollWithProvisioningToken } from '@/lib/deviceEnrollment'

// Role types matching the backend
export type UserRole = 'super_admin' | 'restaurant_admin'

export interface AuthUser {
  role: UserRole
  restaurantId: number | null
  personalAccount: boolean
}

export interface AuthConfiguration {
  cognitoEnabled: boolean
  passwordAdminLoginEnabled: boolean
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  configuration: AuthConfiguration
  login: (username: string, password: string) => Promise<boolean>
  loginWithCognito: () => void
  loginAsRestaurant: (restaurantId: number, accessKey: string) => Promise<boolean>
  logout: () => void
}

interface AuthStatusResponse {
  authenticated?: boolean
  role?: UserRole
  restaurantId?: number | null
  personalAccount?: boolean
}

interface LoginResponse {
  valid?: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

const DEFAULT_AUTH_CONFIGURATION: AuthConfiguration = {
  cognitoEnabled: false,
  passwordAdminLoginEnabled: true,
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [configuration, setConfiguration] = useState<AuthConfiguration>(DEFAULT_AUTH_CONFIGURATION)
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  // navigate/location change identity on every navigation; route them through
  // refs so the context callbacks (and thus the context value) stay stable and
  // consumers don't re-render on every route change.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const locationRef = useRef(location)
  locationRef.current = location

  // Check auth status on mount
  useEffect(() => {
    void checkAuthStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run only on mount
  }, [])

  async function checkAuthStatus() {
    try {
      const loadConfiguration = () => apiClient.get<AuthConfiguration>('/api/admin/auth/config', {
        validateStatus: (status) => status < 500,
      })

      // These requests are independent; starting them together avoids an extra
      // login-page round trip while keeping the backend authoritative. Keep
      // their failures independent: a canceled/failed status request during a
      // browser reload must not hide an otherwise available Cognito login.
      const [configResult, statusResult] = await Promise.allSettled([
        loadConfiguration().catch(loadConfiguration),
        apiClient.get<AuthStatusResponse>('/api/admin/auth/status', {
          validateStatus: (status) => status < 500,
        }),
      ])

      if (configResult.status === 'fulfilled' && configResult.value.status === 200) {
        setConfiguration(configResult.value.data)
      }

      if (statusResult.status === 'rejected') {
        setIsAuthenticated(false)
        setUser(null)
        return
      }

      const response = statusResult.value

      if (response.status === 200 && response.data.authenticated) {
        setIsAuthenticated(true)
        setUser({
          role: response.data.role!,
          restaurantId: response.data.restaurantId ?? null,
          personalAccount: response.data.personalAccount === true,
        })
        return
      }

      // Session invalid — try auto-login with stored credentials
      if (await tryRestaurantAutoLogin()) return
      if (await tryDeviceEnrollment()) return

      setIsAuthenticated(false)
      setUser(null)
    } catch {
      setIsAuthenticated(false)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  async function tryRestaurantAutoLogin(): Promise<boolean> {
    const creds = loadCredentials()
    if (!creds) return false

    try {
      const res = await apiClient.post<LoginResponse>('/api/admin/restaurant-login', {
        restaurantId: creds.restaurantId,
        accessKey: creds.accessKey,
      })
      if (res.data.valid) {
        setIsAuthenticated(true)
        setUser({ role: 'restaurant_admin', restaurantId: creds.restaurantId, personalAccount: false })
        return true
      }
    } catch {
      // Auto-login failed
    }

    clearCredentials()
    return false
  }

  /** Tablet set up by QR: sign in with the token from the provisioning extras, then like a manual login. */
  async function tryDeviceEnrollment(): Promise<boolean> {
    const enrolled = await enrollWithProvisioningToken()
    if (!enrolled) return false
    saveCredentials({ type: 'restaurant', ...enrolled })
    setIsAuthenticated(true)
    setUser({ role: 'restaurant_admin', restaurantId: enrolled.restaurantId, personalAccount: false })
    return true
  }

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await apiClient.post<LoginResponse>('/handleLogin', {
        username,
        password,
      })

      if (response.data.valid) {
        setIsAuthenticated(true)
        setUser({
          role: 'super_admin',
          restaurantId: null,
          personalAccount: false,
        })
        // Redirect to intended destination or dashboard
        const from = (locationRef.current.state as { from?: string } | null)?.from ?? '/'
        navigateRef.current(from, { replace: true })
        return true
      }
      return false
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status && [400, 401, 403].includes(error.response.status)) {
        return false
      }
      throw error
    }
  }, [])

  const loginWithCognito = useCallback(() => {
    window.location.assign(`${API_BASE_URL}/auth/cognito/login`)
  }, [])

  const loginAsRestaurant = useCallback(async (restaurantId: number, accessKey: string): Promise<boolean> => {
    try {
      const response = await apiClient.post<LoginResponse>('/api/admin/restaurant-login', {
        restaurantId,
        accessKey,
      })

      if (response.data.valid) {
        saveCredentials({ type: 'restaurant', restaurantId, accessKey })
        setIsAuthenticated(true)
        setUser({
          role: 'restaurant_admin',
          restaurantId,
          personalAccount: false,
        })
        // Redirect to intended destination or dashboard
        const from = (locationRef.current.state as { from?: string } | null)?.from ?? '/'
        navigateRef.current(from, { replace: true })
        return true
      }
      return false
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status && [400, 401, 403].includes(error.response.status)) {
        return false
      }
      throw error
    }
  }, [])

  const logout = useCallback(() => {
    clearDraftRecoveries()
    clearPosEntryRecoveries()
    clearCredentials()
    setIsAuthenticated(false)
    // Drop all cached server state so the next login starts clean
    queryClient.clear()
    if (user?.role === 'super_admin' && user.personalAccount && configuration.cognitoEnabled) {
      window.location.assign(`${API_BASE_URL}/auth/cognito/logout`)
      return
    }
    setUser(null)
    apiClient.post('/logout').catch(() => { /* noop */ })
    navigateRef.current('/login')
  }, [configuration.cognitoEnabled, queryClient, user?.personalAccount, user?.role])

  // Restaurant tablets report device diagnostics; staff sessions and plain browsers do not.
  const diagnosticsRestaurantId =
    isDeviceDiagnosticsSupported() && user?.role === 'restaurant_admin' ? user.restaurantId : null
  useEffect(() => {
    if (!diagnosticsRestaurantId) return
    let stop: (() => void) | undefined
    let cancelled = false
    // App updates start after diagnostics so their events (applied, rollback) are recorded.
    void startDeviceDiagnostics(diagnosticsRestaurantId)
      .then(async (stopReporter) => {
        // An updater that fails to start must not take the diagnostics reporter down with it.
        const stopUpdates = await startAppUpdates(diagnosticsRestaurantId).catch(() => () => undefined)
        return () => {
          stopUpdates()
          stopReporter()
        }
      })
      .then((stopAll) => {
        if (cancelled) stopAll()
        else stop = stopAll
      })
    return () => {
      cancelled = true
      stop?.()
    }
  }, [diagnosticsRestaurantId])

  const value = useMemo(
    () => ({ isAuthenticated, isLoading, user, configuration, login, loginWithCognito, loginAsRestaurant, logout }),
    [isAuthenticated, isLoading, user, configuration, login, loginWithCognito, loginAsRestaurant, logout]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Protected route wrapper
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { state: { from: location.pathname }, replace: true })
    }
  }, [isAuthenticated, isLoading, location, navigate])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return isAuthenticated ? <>{children}</> : null
}
